const API_BASE = '/api/lingua-snaps';

async function getAuthHeaders() {
  const token = localStorage.getItem('token') || '';
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

async function fetchWithAuth(url, options = {}) {
  const headers = await getAuthHeaders();
  const res = await fetch(url, { ...options, headers: { ...headers, ...options.headers } });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
}

// Get signed upload URL
export async function getUploadUrl(filename) {
  return fetchWithAuth(`${API_BASE}/upload-url`, {
    method: 'POST',
    body: JSON.stringify({ filename }),
  });
}

// Get predefined music tracks for image_music snaps
export async function getTracks() {
  const res = await fetch(`${API_BASE}/tracks`);
  if (!res.ok) throw new Error('Failed to fetch tracks');
  return res.json();
}

// Create snap after upload
// For video: { originalVideoPath, caption?, targetLanguage?, cefrLevel?, maxDurationSeconds? }
// For image_music: { sourceType: 'image_music', originalImagePath, musicTrackId, caption?, targetLanguage?, cefrLevel?, maxDurationSeconds? }
export async function createSnap(params) {
  return fetchWithAuth(`${API_BASE}/create`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// Trigger transcoding
export async function processSnap(snapId) {
  return fetchWithAuth(`${API_BASE}/${snapId}/process`, { method: 'POST' });
}

// Publish snap
export async function publishSnap(snapId) {
  return fetchWithAuth(`${API_BASE}/${snapId}/publish`, { method: 'POST' });
}

// Delete snap
export async function deleteSnap(snapId) {
  return fetchWithAuth(`${API_BASE}/${snapId}`, { method: 'DELETE' });
}

// Get single snap with details
export async function getSnap(snapId) {
  return fetchWithAuth(`${API_BASE}/${snapId}`);
}

// Get home feed (grouped by user)
export async function getHomeFeed(limit = 50, includeSelf = true) {
  return fetchWithAuth(`${API_BASE}/home?limit=${limit}&includeSelf=${includeSelf}`);
}

// Get my snaps
export async function getMySnaps(includeExpired = false) {
  return fetchWithAuth(`${API_BASE}/my?includeExpired=${includeExpired}`);
}

// Get user's active snaps (for profile)
export async function getUserSnaps(userId) {
  return fetchWithAuth(`${API_BASE}/user/${userId}`);
}

// Get active count
export async function getActiveCount() {
  return fetchWithAuth(`${API_BASE}/active-count`);
}

// Record view
export async function recordView(snapId, watchMs = null, sessionId = null) {
  return fetchWithAuth(`${API_BASE}/${snapId}/view`, {
    method: 'POST',
    body: JSON.stringify({ watchMs, sessionId }),
  });
}

// Toggle reaction
export async function toggleReaction(snapId, reaction) {
  return fetchWithAuth(`${API_BASE}/${snapId}/react`, {
    method: 'POST',
    body: JSON.stringify({ reaction }),
  });
}

// Get reactions
export async function getReactions(snapId) {
  return fetchWithAuth(`${API_BASE}/${snapId}/reactions`);
}

// Add comment
export async function addComment(snapId, body) {
  return fetchWithAuth(`${API_BASE}/${snapId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

// Get comments
export async function getComments(snapId, limit = 50) {
  return fetchWithAuth(`${API_BASE}/${snapId}/comments?limit=${limit}`);
}

// Delete comment
export async function deleteComment(commentId) {
  return fetchWithAuth(`${API_BASE}/comments/${commentId}`, { method: 'DELETE' });
}

// Upload video file to signed URL
export async function uploadVideoToSignedUrl(signedUrl, file, onProgress = null) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl, true);
    xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

// Upload file (video or image) to signed URL
export async function uploadFileToSignedUrl(signedUrl, file, onProgress = null) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

// Helper: full upload + create + process + publish flow for VIDEO snaps
export async function uploadAndPublishSnap({ file, caption, targetLanguage, cefrLevel, maxDurationSeconds, onProgress, onStatus }) {
  onStatus?.('Getting upload URL...');
  const { uploadUrl, path } = await getUploadUrl(file.name);

  onStatus?.('Uploading video...');
  await uploadVideoToSignedUrl(uploadUrl, file, onProgress);

  onStatus?.('Creating snap...');
  const { snap } = await createSnap({
    sourceType: 'video',
    originalVideoPath: path,
    caption,
    targetLanguage,
    cefrLevel,
    maxDurationSeconds,
  });

  onStatus?.('Processing video...');
  const { snap: processedSnap } = await processSnap(snap.id);

  onStatus?.('Publishing...');
  const { snap: publishedSnap } = await publishSnap(processedSnap.id);

  onStatus?.('Done!');
  return publishedSnap;
}

// Helper: full upload + create + process + publish flow for IMAGE + MUSIC snaps
export async function uploadAndPublishImageMusicSnap({ imageFile, musicTrackId, caption, targetLanguage, cefrLevel, maxDurationSeconds, onProgress, onStatus }) {
  onStatus?.('Getting upload URL...');
  const { uploadUrl, path } = await getUploadUrl(imageFile.name);

  onStatus?.('Uploading image...');
  await uploadFileToSignedUrl(uploadUrl, imageFile, onProgress);

  onStatus?.('Creating snap...');
  const { snap } = await createSnap({
    sourceType: 'image_music',
    originalImagePath: path,
    musicTrackId,
    caption,
    targetLanguage,
    cefrLevel,
    maxDurationSeconds,
  });

  onStatus?.('Generating video from image + music...');
  const { snap: processedSnap } = await processSnap(snap.id);

  onStatus?.('Publishing...');
  const { snap: publishedSnap } = await publishSnap(processedSnap.id);

  onStatus?.('Done!');
  return publishedSnap;
}

// Calculate time remaining for snap expiry
export function getTimeRemaining(expiresAt) {
  if (!expiresAt) return null;
  const now = new Date();
  const expires = new Date(expiresAt);
  const diff = expires - now;
  if (diff <= 0) return 'Expired';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

// Check if snap is active
export function isSnapActive(snap) {
  if (!snap) return false;
  if (snap.status !== 'published') return false;
  if (!snap.expires_at) return false;
  return new Date(snap.expires_at) > new Date();
}
